import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import type { OrgTheme, ThemeMode } from '@/types/theme'
import { defaultColors, defaultColorsLight, defaultGlassSettings } from '@/types/theme'

const THEME_STORAGE_KEY = 'tlh-theme-mode'

export const useThemeStore = defineStore('theme', () => {
  // Состояние
  const mode = ref<ThemeMode>('auto')
  const systemPrefersDark = ref(false)
  const orgTheme = ref<OrgTheme | null>(null)

  // Computed
  const isDark = computed(() => {
    if (mode.value === 'auto') {
      return systemPrefersDark.value
    }
    return mode.value === 'dark'
  })

  const currentTheme = computed(() => (isDark.value ? 'glassDark' : 'glassLight'))

  const colors = computed(() => {
    // Если есть тема организации, используем её цвета
    if (orgTheme.value) {
      // Для светлой темы используем те же цвета, но с другим фоном
      if (!isDark.value) {
        return {
          ...orgTheme.value.colors,
          background: defaultColorsLight.background,
          surface: defaultColorsLight.surface,
        }
      }
      return orgTheme.value.colors
    }
    return isDark.value ? defaultColors : defaultColorsLight
  })

  const glassSettings = computed(() => orgTheme.value?.glass ?? defaultGlassSettings)

  // Действия
  function initTheme() {
    // Загрузить сохранённую тему
    try {
      const saved = localStorage.getItem(THEME_STORAGE_KEY) as ThemeMode | null
      if (saved && ['light', 'dark', 'auto'].includes(saved)) {
        mode.value = saved
      }
    } catch (e) {
      console.warn('[Theme] Failed to read from localStorage:', e)
    }

    // Отслеживать системные предпочтения
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    systemPrefersDark.value = mediaQuery.matches

    mediaQuery.addEventListener('change', (e) => {
      systemPrefersDark.value = e.matches
    })

    // Применить CSS-переменные
    applyThemeVariables()
  }

  function setMode(newMode: ThemeMode) {
    mode.value = newMode
    try {
      localStorage.setItem(THEME_STORAGE_KEY, newMode)
    } catch (e) {
      console.warn('[Theme] Failed to save to localStorage:', e)
    }
    applyThemeVariables()
  }

  function toggleTheme() {
    if (mode.value === 'auto') {
      setMode(systemPrefersDark.value ? 'light' : 'dark')
    } else {
      setMode(mode.value === 'dark' ? 'light' : 'dark')
    }
  }

  function setOrgTheme(theme: OrgTheme) {
    orgTheme.value = theme
    applyThemeVariables()
  }

  function applyThemeVariables() {
    const root = document.documentElement
    const c = colors.value
    const g = glassSettings.value

    // Цвета
    root.style.setProperty('--tlh-primary', c.primary)
    root.style.setProperty('--tlh-secondary', c.secondary)
    root.style.setProperty('--tlh-accent', c.accent)
    root.style.setProperty('--tlh-background', c.background)
    root.style.setProperty('--tlh-surface', c.surface)
    root.style.setProperty('--tlh-error', c.error)
    root.style.setProperty('--tlh-warning', c.warning)
    root.style.setProperty('--tlh-success', c.success)
    root.style.setProperty('--tlh-info', c.info)

    // Glassmorphism
    root.style.setProperty('--tlh-glass-blur', `${g.blur}px`)
    root.style.setProperty('--tlh-glass-opacity', String(g.opacity))
    root.style.setProperty('--tlh-glass-saturation', `${g.saturation}%`)
    root.style.setProperty(
      '--tlh-glass-border',
      g.border ? '1px solid rgba(255, 255, 255, 0.1)' : 'none'
    )
  }

  // Наблюдать за изменениями темы
  watch(isDark, () => {
    applyThemeVariables()
  })

  return {
    // Состояние
    mode,
    orgTheme,
    // Computed
    isDark,
    currentTheme,
    colors,
    glassSettings,
    // Действия
    initTheme,
    setMode,
    toggleTheme,
    setOrgTheme,
  }
})
